import { useEffect, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import classNames from 'classnames'
import * as R from 'ramda'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import {
  getIsRetailOrderLineItem,
  getTotal,
  isGroupedInvoiceItem,
  quantityFieldChanged,
  setHighValue,
  setLowValue,
} from '~/components/dashboard/invoices/invoiceUtils'
import { ConfirmAlertType } from '~/constants/DialogNames'
import {
  removeLineItems,
  toggleInvoiceGroupExpanded,
} from '~/store/actions/finance'
import { getClientFinanceLoading } from '~/store/duck/clientFinanceData'
import { useGetEstimateState } from '~/store/hooks/estimate'
import { getIsSetBundlePriceEnabled } from '~/store/reducers/constants'
import { getFinanceIsFetching } from '~/store/reducers/finance'
import { getOrdersIsSending } from '~/store/reducers/orders'
import type { Estimate, InvoiceLineItem, InvoiceLineItemGroup } from '~/types'

import ChirpEstimateTableRow from './ChirpEstimateTableRow'

import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import styles from './ChirpEstimateTable.module.scss'

export interface ChirpEstimateTableProps {
  editDisabled?: boolean
  invoice: Estimate
  updateInvoice: (invoice: Estimate) => void
}

type SubTotalsUpdateType = {
  isGroup: boolean
  isGroupedItem: boolean
  itemIndex: number
  itemIndexInParent: number
  itemParent: InvoiceLineItem
  newItem: InvoiceLineItem
}

const ChirpEstimateTable = ({
  editDisabled = false,
  invoice,
  updateInvoice,
}: ChirpEstimateTableProps) => {
  const { t } = useTranslation(['Common', 'Payments'])
  const dispatch = useDispatch()
  const isFetching = useSelector(getFinanceIsFetching)
  const isFetchingChargeSheet = useSelector(getClientFinanceLoading)
  const isSendingOrders = useSelector(getOrdersIsSending)
  const isSetBundlePriceEnabled = useSelector(getIsSetBundlePriceEnabled)
  const estimateState = useGetEstimateState()(invoice?.stateId)

  const [openConfirmAlert] = useConfirmAlert({
    type: ConfirmAlertType.INVOICE_TABLE_ITEM,
  })

  // Match old table loading logic
  const isLoading = isFetching || isSendingOrders || isFetchingChargeSheet

  // Check if estimate is approved or declined (read-only state)
  const isReadOnly =
    editDisabled || estimateState.isApproved || estimateState.isDeclined

  // Get items from invoice groups
  const groups = invoice?.groups || []
  const group = groups[0] // Estimates have single group
  const items = group?.groupedItems || []

  const [localItems, setLocalItems] = useState<
    (InvoiceLineItem | RetailOrderLineItem)[]
  >([])

  // Update local items when invoice changes
  // Initialize state with empty array to avoid infinite loops during initial load
  useEffect(() => {
    setLocalItems(items)
  }, [JSON.stringify(items)])

  // Helper to check if item is grouped within a bundle
  const getIsGroupedItem = (
    item: InvoiceLineItem,
    currentGroup: InvoiceLineItemGroup,
  ) =>
    currentGroup.groupedItems.some((groupedItem) =>
      groupedItem.items?.includes(item),
    )

  // Delete action handler with confirmation
  const deleteItem = (item: InvoiceLineItem) => {
    const isGroupedItem = getIsGroupedItem(item, group)
    const isGroup = Boolean(item.items)

    const itemParent: InvoiceLineItem | null =
      isGroupedItem && !isGroup
        ? (group.groupedItems.find((groupItem) =>
            groupItem.items?.includes(item),
          ) as InvoiceLineItem)
        : null

    const itemIndex = itemParent ? group.groupedItems.indexOf(itemParent) : -1
    const groupIndex = groups.indexOf(group)

    const newParentItem: InvoiceLineItem | null =
      isGroupedItem && !isGroup
        ? (() => {
            const updatedParent = {
              ...(itemParent as InvoiceLineItem),
              items: R.without([item], itemParent?.items || []),
              fixedPrice: null,
            }
            // Recalculate parent bundle's subTotal after removing child (sum of remaining items)
            if (updatedParent.items && updatedParent.items.length > 0) {
              updatedParent.subTotal = getTotal(
                updatedParent,
                invoice,
                isSetBundlePriceEnabled,
              ) as number
            }
            return updatedParent
          })()
        : null

    // Determine updated groupedItems after deletion
    const getUpdatedGroupedItems = () => {
      // If parent bundle is now empty, remove the entire bundle
      if (newParentItem && !newParentItem.items?.length) {
        return R.without([itemParent], group.groupedItems)
      }
      // If parent bundle still has items, update it
      if (newParentItem) {
        return R.update(itemIndex, newParentItem, group.groupedItems)
      }
      // Otherwise, remove the individual item
      return R.without([item], group.groupedItems)
    }

    const newGroup = {
      ...group,
      groupedItems: getUpdatedGroupedItems(),
    } as InvoiceLineItemGroup

    const newGroups: InvoiceLineItemGroup[] =
      newGroup.groupedItems.length === 0
        ? R.without([group], groups)
        : R.update(groupIndex, newGroup, groups)

    const bundleAdditionalDiscount =
      (invoice.bundleAdditionalDiscount || 0) - (item.additionalDiscount || 0)
    updateInvoice({ ...invoice, groups: newGroups, bundleAdditionalDiscount })

    if (invoice.id) {
      const idsToRemove = R.pluck('id', item.items || [item])
      dispatch(removeLineItems(idsToRemove, invoice.id))
    }
  }

  // Update subtotals
  const updateSubtotals = (subTotalsUpdate: SubTotalsUpdateType) => {
    subTotalsUpdate.newItem.subTotal = getTotal(
      subTotalsUpdate.newItem,
      invoice,
      isSetBundlePriceEnabled,
    ) as number

    // If this is a child item in a bundle, update parent bundle totals too
    const newParentItem =
      subTotalsUpdate.isGroupedItem && !subTotalsUpdate.isGroup
        ? (() => {
            const updatedParent = {
              ...subTotalsUpdate.itemParent,
              items: R.update(
                subTotalsUpdate.itemIndexInParent,
                subTotalsUpdate.newItem,
                subTotalsUpdate.itemParent.items || [],
              ),
              fixedPrice: null,
            }
            // Recalculate parent bundle's subTotal based on updated children (sum, not set price)
            updatedParent.subTotal = getTotal(
              updatedParent,
              invoice,
              isSetBundlePriceEnabled,
            ) as number
            return updatedParent
          })()
        : subTotalsUpdate.newItem

    const invoiceGroupIndex = groups.indexOf(group)
    const newGroup = {
      ...group,
      groupedItems: R.update(
        subTotalsUpdate.itemIndex,
        newParentItem,
        group.groupedItems,
      ),
    }
    updateInvoice({
      ...invoice,
      groups: R.update(invoiceGroupIndex, newGroup, groups),
    })
  }

  // Decline toggle action handler
  const toggleDecline = (item: InvoiceLineItem) => {
    const isGroupedItem = getIsGroupedItem(item, group)
    const isGroup = Boolean(item.items)
    const newDeclinedValue = !item.declined

    const itemParent =
      isGroupedItem && !isGroup
        ? (group.groupedItems.find((groupItem) =>
            groupItem.items?.includes(item),
          ) as InvoiceLineItem)
        : item

    const itemIndexInParent = isGroupedItem
      ? itemParent.items!.indexOf(item)
      : -1
    const itemIndex = group.groupedItems.indexOf(itemParent)

    const newItem = isGroup
      ? {
          ...item,
          declined: newDeclinedValue,
          items: item.items?.map((childItem) => ({
            ...childItem,
            declined: newDeclinedValue,
          })),
        }
      : { ...item, declined: newDeclinedValue }

    // Update subTotal after decline status change
    updateSubtotals({
      newItem,
      itemParent,
      isGroupedItem,
      isGroup,
      itemIndexInParent,
      itemIndex,
    })
  }

  // Quantity change handler with recalculations
  const handleQuantityChange = (
    item: InvoiceLineItem | RetailOrderLineItem,
    quantityLow: number,
    quantityHigh: number,
  ) => {
    // Estimates don't use RetailOrderLineItem, safe to cast
    const invoiceItem = item as InvoiceLineItem
    const isGroupedItem = getIsGroupedItem(invoiceItem, group)
    const isGroup = Boolean(invoiceItem.items)

    const itemParent =
      isGroupedItem && !isGroup
        ? (group.groupedItems.find((groupItem) =>
            groupItem.items?.includes(invoiceItem),
          ) as InvoiceLineItem)
        : invoiceItem

    const itemIndexInParent = isGroupedItem
      ? itemParent.items!.indexOf(invoiceItem)
      : -1
    const itemIndex = group.groupedItems.indexOf(itemParent)

    // Update quantity value - treat as Amount for estimates
    // Use normalize=true to ensure low <= high
    const itemQuantity = invoiceItem.quantity as
      | number
      | { high: number; low: number }
    const newQuantity =
      quantityLow === quantityHigh
        ? quantityLow
        : setHighValue(
            setLowValue(itemQuantity, quantityLow, true),
            quantityHigh,
            true,
          )

    const newItem = { ...invoiceItem, quantity: newQuantity as number }

    // Recalculate dependent fields - treat newItem as compatible type for calculations
    quantityFieldChanged(invoiceItem as any, newItem as any, true)

    // Update subTotal to reflect in UI immediately
    updateSubtotals({
      newItem,
      itemParent,
      isGroupedItem,
      isGroup,
      itemIndexInParent,
      itemIndex,
    })
  }

  // Delete with confirmation dialog
  const handleDeleteClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    // Estimates don't use RetailOrderLineItem, safe to cast
    const invoiceItem = item as InvoiceLineItem
    const isBundle = isGroupedInvoiceItem(invoiceItem)
    openConfirmAlert({
      message: t('Common:YOU_WILL_REMOVE_SOMETHING', {
        something: isBundle ? invoiceItem.groupName : invoiceItem.name,
      }),
      onConfirm: (proceed: boolean) => proceed && deleteItem(invoiceItem),
    })
  }

  // Decline wrapper for row callbacks
  const handleDeclineClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    // Estimates don't use RetailOrderLineItem, safe to cast
    toggleDecline(item as InvoiceLineItem)
  }

  // Get item ID for drag and drop - must be unique
  const getItemId = (
    item: InvoiceLineItem | RetailOrderLineItem,
    index: number,
  ) => {
    if (isGroupedInvoiceItem(item)) {
      const invoiceItem = item as InvoiceLineItem
      // Use group or fallback to id or index
      const groupKey = invoiceItem.group || invoiceItem.id || `idx-${index}`
      return `estimate-table-group-${invoice?.id || 'no-estimate'}_${groupKey}`
    }
    // Use id + logId, or just id, or fallback to index
    const itemKey = item.id || `idx-${index}`
    const logKey = item.logId || 'no-log'
    return `estimate-table-${itemKey}_${logKey}`
  }

  // Handle drag end for reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !invoice?.id) return

    const from = result.source.index
    const to = result.destination.index

    if (from === to) return

    const newItemsOrder = R.move(from, to, localItems)
    setLocalItems(newItemsOrder)

    // Update invoice with new order
    const invoiceGroupIndex = groups.indexOf(group)
    const newGroup = {
      ...group,
      groupedItems: newItemsOrder as InvoiceLineItem[],
    }
    updateInvoice({
      ...invoice,
      groups: R.update(invoiceGroupIndex, newGroup, groups),
    })
  }

  // Toggle bundle expand/collapse
  const handleToggleExpand = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const groupId = getIsRetailOrderLineItem(item) ? null : item.group
    dispatch(toggleInvoiceGroupExpanded(groupId || item.id, invoice?.id))
  }

  // Column definitions for skeleton rendering
  const columns = [
    { key: 'item', className: styles.dataCellItem },
    { key: 'quantityLow', className: styles.dataCellQuantityLow },
    { key: 'quantityHigh', className: styles.dataCellQuantityHigh },
    { key: 'actions', className: styles.dataCellActions },
    { key: 'totalLow', className: styles.dataCellTotalLow },
    { key: 'totalHigh', className: styles.dataCellTotalHigh },
  ]

  // Number of skeleton rows to show while loading (including bundle children)
  const skeletonRowCount =
    localItems.reduce((count, item) => {
      const isBundle = isGroupedInvoiceItem(item)
      const isRetailOrder = getIsRetailOrderLineItem(item)
      const bundleChildCount =
        isBundle && !isRetailOrder ? (item.items?.length ?? 0) : 0
      return count + 1 + bundleChildCount
    }, 0) || 3

  // Determine if we have items to display
  const hasItems = items && items.length > 0

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <tbody data-testid="estimate-table-skeleton">
          {Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
            <tr className={styles.skeletonRow} key={`skeleton-${rowIndex}`}>
              {columns.map((col) => (
                <td className={col.className} key={col.key}>
                  <KibSkeletonText lines={1} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      )
    }

    return hasItems ? (
      <Droppable droppableId="estimate-table" type="ESTIMATE">
        {(droppableProvided) => (
          <tbody
            ref={(ref) => {
              droppableProvided.innerRef(ref)
            }}
            {...droppableProvided.droppableProps}
          >
            {localItems.map((item, index) => {
              const itemId = getItemId(item, index)
              const isBundle = isGroupedInvoiceItem(item)
              const isRetailOrder = getIsRetailOrderLineItem(item)
              const bundleItems =
                isBundle && !isRetailOrder ? (item.items ?? []) : []

              return (
                <Draggable
                  draggableId={itemId}
                  index={index}
                  isDragDisabled={isReadOnly}
                  key={itemId}
                >
                  {(draggableProvided, draggableSnapshot) => (
                    <ChirpEstimateTableRow
                      bundleItems={bundleItems}
                      dragHandleProps={draggableProvided.dragHandleProps}
                      draggableProps={draggableProvided.draggableProps}
                      editDisabled={isReadOnly}
                      estimateId={invoice?.id}
                      isBundle={isBundle}
                      isDragging={draggableSnapshot.isDragging}
                      item={item}
                      ref={draggableProvided.innerRef}
                      onDeclineClick={handleDeclineClick}
                      onDeleteClick={handleDeleteClick}
                      onQuantityChange={handleQuantityChange}
                      onToggleExpand={handleToggleExpand}
                    />
                  )}
                </Draggable>
              )
            })}
            {droppableProvided.placeholder}
          </tbody>
        )}
      </Droppable>
    ) : (
      <tbody data-testid="estimate-table-empty">
        <tr>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <td colSpan={6} />
        </tr>
      </tbody>
    )
  }

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableWrapper}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              {/* Main header row */}
              <tr className={styles.headerRow}>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellItem,
                  )}
                >
                  {t('Common:ITEM')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellQuantity,
                  )}
                  colSpan={2}
                >
                  {t('Common:QUANTITY')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellActions,
                  )}
                >
                  {t('Common:ACTIONS')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellTotal,
                  )}
                  colSpan={2}
                >
                  {t('Common:TOTAL')}
                </th>
              </tr>
              {/* Sub-header row for Low/High labels */}
              <tr className={styles.subHeaderRow}>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <th
                  className={classNames(
                    styles.subHeaderCellEmpty,
                    styles.subHeaderCellItem,
                  )}
                />
                <th
                  className={classNames(
                    styles.subHeaderCell,
                    styles.subHeaderCellLow,
                  )}
                >
                  {t('Common:LOW')}
                </th>
                <th
                  className={classNames(
                    styles.subHeaderCell,
                    styles.subHeaderCellHigh,
                  )}
                >
                  {t('Common:HIGH')}
                </th>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <th
                  className={classNames(
                    styles.subHeaderCellEmpty,
                    styles.subHeaderCellActions,
                  )}
                />
                <th
                  className={classNames(
                    styles.subHeaderCell,
                    styles.subHeaderCellTotalLow,
                  )}
                >
                  {t('Common:LOW')}
                </th>
                <th
                  className={classNames(
                    styles.subHeaderCell,
                    styles.subHeaderCellTotalHigh,
                  )}
                >
                  {t('Common:HIGH')}
                </th>
              </tr>
            </thead>

            {/* Table content */}
            {renderTableContent()}
          </table>
        </DragDropContext>
      </div>
    </div>
  )
}

export default ChirpEstimateTable
